import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { useTranslation } from '@apple/lib/i18next';
import { BlockNavigation } from '@apple/ui/form/components/BlockNavigation';
import { ServerValidatedZodForm } from '@apple/ui/form/components/ValidatedForm/ServerValidatedZodForm';
import { StepLayout } from '@apple/ui/layouts/step';
import { useLogger } from '@apple/utils/logging/useLogger';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import type { StoreListingModel } from '@apple/features/store/models';
import { StoreEventDetails } from '@apple/features/store-events/components/details';
import { ShipmentReportLink } from '@apple/features/store-events/components/shipment-report-link';
import { useCreateStoreEvent } from '@apple/features/store-events/hooks/use-create-store-event';
import {
	DEFAULT_STORE_EVENT_UPDATE_MODEL,
	storeEventUpdateModelSchema,
} from '@apple/features/store-events/models/management';
import { storeEventQueries } from '@apple/features/store-events/queries';

export const Route = createFileRoute('/_authed/store-events/_steps/new')({
	beforeLoad: args =>
		requireAuth(args, {
			requirePermission: 'AppleRetail.Security.Features.StoreEventManagement.Write',
		}),
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation('store-event');
	const navigate = Route.useNavigate();
	const [selectedStore, setSelectedStore] = useState<StoreListingModel>();
	const createMutation = useCreateStoreEvent();

	const storeIdsForExistingQuery = useSuspenseQuery(
		storeEventQueries.getStoreIdsForExistingEvents(),
	);

	useLogger({
		log: 'store-events',
		name: 'StoreEventDetails',
		props: [{ selectedStore }],
	});

	return (
		<ServerValidatedZodForm
			name={'store-event-create'}
			validationType='EditEventDetails'
			schema={storeEventUpdateModelSchema}
			initialValues={DEFAULT_STORE_EVENT_UPDATE_MODEL}
			onValidSubmit={async storeEvent => {
				if (!selectedStore) return;
				await createMutation.mutateAsync(
					{
						storeId: selectedStore.thirdPartyId,
						storeEvent,
					},
					{
						onSuccess: async ({ id }) => {
							await navigate({
								to: '/store-events/$storeEventId/categories',
								params: {
									storeEventId: id,
								},
							});
						},
					},
				);
			}}
			onAdditionalValidation={() => {
				if (!selectedStore) {
					return 'Store is required';
				}
			}}
			ignoreModelErrorParent
		>
			{({ form, submit, isSubmitting }) => (
				<StepLayout
					mutationErrors={[createMutation.error]}
					buttonLeft={{
						disabled: true,
					}}
					buttonRight={{
						text: t('common:buttons.create'),
						color: 'green',
						loading: createMutation.isPending || isSubmitting,
						disabled:
							createMutation.isPending || isSubmitting || selectedStore === undefined,
						onClick: submit,
					}}
				>
					<BlockNavigation
						condition={
							// Stop navigation when the form is dirty and not saved
							form.isDirty() &&
							!(createMutation.isPending || createMutation.isSuccess || isSubmitting)
						}
					/>
					<StoreEventDetails
						form={form}
						storeId={selectedStore?.thirdPartyId}
						onStoreAssigned={setSelectedStore}
						excludeStoreIds={storeIdsForExistingQuery.data}
						storeUtcOffsetMinutes={selectedStore?.utcOffsetMinutes}
						renderShipmentReportLink={ShipmentReportLink}
					/>
				</StepLayout>
			)}
		</ServerValidatedZodForm>
	);
}
